import axios from 'axios';
import {
  MIN_TTL,
  AUTH_DOMAIN_DELIVERABILITY,
  AUTH_DOMAIN_BRANDED_LINKS,
  MIN_DELTA_VERIFY_CALL_MIN,
} from '@/Email/config/email-management';

const mapDomainAuthToArray = domainAuth => {
  if (!domainAuth) return [];
  return [
    {
      dns_type: domainAuth.spf.type,
      name: domainAuth.spf.host,
      data: domainAuth.spf.data,
      ttl: MIN_TTL,
      verified: domainAuth.spf.valid,
      required_for: AUTH_DOMAIN_DELIVERABILITY,
    },
    {
      dns_type: domainAuth.dkim1.type,
      name: domainAuth.dkim1.host,
      data: domainAuth.dkim1.data,
      ttl: MIN_TTL,
      verified: domainAuth.dkim1.valid,
      required_for: AUTH_DOMAIN_DELIVERABILITY,
    },
    {
      dns_type: domainAuth.dkim2.type,
      name: domainAuth.dkim2.host,
      data: domainAuth.dkim2.data,
      ttl: MIN_TTL,
      verified: domainAuth.dkim2.valid,
      required_for: AUTH_DOMAIN_DELIVERABILITY,
    },
    {
      dns_type: domainAuth.dmarc.type,
      name: domainAuth.dmarc.host,
      data: domainAuth.dmarc.data,
      ttl: MIN_TTL,
      verified: domainAuth.dmarc.valid,
      required_for: AUTH_DOMAIN_DELIVERABILITY,
    },
  ];
};

const mapBrandedLinksToArray = brandedLinks => {
  if (!brandedLinks) return [];
  return [
    {
      dns_type: brandedLinks.domain.type,
      name: brandedLinks.domain.host,
      data: brandedLinks.domain.data,
      ttl: MIN_TTL,
      verified: brandedLinks.domain.valid,
      required_for: AUTH_DOMAIN_BRANDED_LINKS,
    },
    {
      dns_type: brandedLinks.owner.type,
      name: brandedLinks.owner.host,
      data: brandedLinks.owner.data,
      ttl: MIN_TTL,
      verified: brandedLinks.owner.valid,
      required_for: AUTH_DOMAIN_BRANDED_LINKS,
    },
  ];
};

const initialState = {
  domainAuthInfo: null,
  systemDomain: {
    domain: null,
    complimentary_email: null,
  },
  error: null,
  lastVerifyTime: null,
  domainAuth: {
    successCreated: false,
    errorCreated: null,
    loadingCreated: false,
    successVerified: false,
    errorVerified: null,
    loadingVerify: false,
  },
  brandedLink: {
    successCreated: false,
    errorCreated: null,
    loadingCreated: false,
    successVerified: false,
    errorVerified: null,
    loadingVerify: false,
  },
  sendInfoTo3rdPartySuccess: false,
  sendInfoTo3rdPartyError: null,
  sendInfoTo3rdPartyLoading: false,
};

export default {
  namespaced: true,
  state: { ...initialState },
  mutations: {
    setError(state, error) {
      state.error = error;
    },
    setNowtLastVerifyTime(state) {
      state.lastVerifyTime = Date.now();
    },
    setDomainAuthInfo(state, info) {
      if (!info) {
        state.domainAuthInfo = info;
      }
      state.domainAuthInfo = {
        ...state.domainAuthInfo,
        ...info,
      };
    },
    setSystemDomain(state, sysDomainData) {
      state.systemDomain = {
        ...state.systemDomain,
        ...sysDomainData,
      };
    },
    setDomainAuthCreated(state, created) {
      state.domainAuth.successCreated = created;
    },
    setDomainAuthCreatedLoading(state, loading) {
      state.domainAuth.loadingCreated = loading;
    },
    setDomainAuthCreatedError(state, error) {
      state.domainAuth.errorCreated = error;
      state.domainAuth.successCreated = !error;
      state.domainAuth.loadingCreated = false;
    },
    setDomainAuthVerified(state, verified) {
      state.domainAuth.successVerified = verified;
    },
    setDomainAuthVerifiedLoading(state, loading) {
      state.domainAuth.loadingVerify = loading;
    },
    setDomainAuthVerifiedError(state, error) {
      state.domainAuth.errorVerified = error;
      state.domainAuth.successVerified = !error;
      state.domainAuth.loadingVerify = false;
    },
    setBrandedLinkCreated(state, created) {
      state.brandedLink.successCreated = created;
    },
    setBrandedLinkCreatedLoading(state, loading) {
      state.brandedLink.loadingCreated = loading;
    },
    setBrandedLinkCreatedError(state, error) {
      state.brandedLink.errorCreated = error;
      state.brandedLink.successCreated = !error;
      state.brandedLink.loadingCreated = false;
    },
    setBrandedLinkVerified(state, verified) {
      state.brandedLink.successVerified = verified;
    },
    setBrandedLinkVerifiedLoading(state, loading) {
      state.brandedLink.loadingVerify = loading;
    },
    setBrandedLinkVerifiedError(state, error) {
      state.brandedLink.errorVerified = error;
      state.brandedLink.successVerified = !error;
      state.brandedLink.loadingVerify = false;
    },
    setInfoTo3rdPartySucess(state, status) {
      state.sendInfoTo3rdPartySuccess = status;
    },
    setInfoTo3rdPartyError(state, error) {
      state.sendInfoTo3rdPartyError = error;
    },
    setInfoTo3rdPartyLoading(state, value) {
      state.sendInfoTo3rdPartyLoading = value;
    },
    resetInfoTo3rdPartySucess(state) {
      state.sendInfoTo3rdPartySuccess = false;
    },
    resetInfoTo3rdPartyError(state) {
      state.sendInfoTo3rdPartyError = false;
    },
    resetInfoTo3rdPartyLoading(state) {
      state.sendInfoTo3rdPartyLoading = false;
    },
  },
  getters: {
    hasError(state) {
      return !!state.error;
    },
    hasAuthenticatedDomain(state) {
      return (
        state.domainAuthInfo &&
        state.domainAuthInfo.domain_auth &&
        state.domainAuthInfo.domain_auth.valid
      );
    },
    allowedNoAuthenticatedDomain(state) {
      if (!state.domainAuthInfo) return false;

      const excludedDomains = process.env.VUE_APP_NO_AUTH_EXCLUDED_DOMAINS.split(',');
      const parts = state.domainAuthInfo.domain.split('.');
      return !excludedDomains.find(domain => domain === parts[0]);
    },
    authDomainInfoEmailManagement(state) {
      if (!state.domainAuthInfo) return [];

      const { domain_auth: domainAuth, branded_links: brandedLinks } = state.domainAuthInfo;
      return [...mapDomainAuthToArray(domainAuth), ...mapBrandedLinksToArray(brandedLinks)];
    },
    allowCallVerify(state) {
      if (!state.lastVerifyTime) return true;

      const currentTime = Date.now();
      const delta = Math.floor((currentTime - state.lastVerifyTime) / (1000 * 60));
      return delta > MIN_DELTA_VERIFY_CALL_MIN;
    },
    isLoadingCreated(state) {
      return state.brandedLink.loadingCreated || state.domainAuth.loadingCreated;
    },
    hasCreatedError(state) {
      return !!state.brandedLink.errorCreated || !!state.domainAuth.errorCreated;
    },
    getCreatedError(state) {
      return state.domainAuth.errorCreated || state.brandedLink.errorCreated;
    },
    isLoadingVerify(state) {
      return state.brandedLink.loadingVerify || state.domainAuth.loadingVerify;
    },
    hasVerifyError(state) {
      return !!state.brandedLink.errorVerified || !!state.domainAuth.errorVerified;
    },
    getVerifyError(state) {
      return state.domainAuth.errorVerified || state.brandedLink.errorVerified;
    },
    isSuccessCreated(state) {
      return state.domainAuth.successCreated && state.brandedLink.successCreated;
    },
    isSuccessVerified(state) {
      return state.domainAuth.successVerified && state.brandedLink.successVerified;
    },
  },
  actions: {
    async getAuthDomainInfo({ commit }) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/email/domainauth/info`,
        );

        commit('setDomainAuthInfo', response.data);
      } catch (error) {
        if (error.data && error.data.error) {
          commit('setError', error.data.error);
          return;
        }

        commit('setError', error);
      }
    },
    async createDomainAuth({ commit }, { payload }) {
      commit('setDomainAuthCreatedLoading', true);
      try {
        await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/email/domainauth/domain-auth`,
          payload,
        );
        commit('setDomainAuthCreated', true);
        commit('setDomainAuthCreatedLoading', false);
      } catch (error) {
        if (error.data && error.data.error) {
          commit('setDomainAuthCreatedError', error.data.error);
          return;
        }

        commit('setDomainAuthCreatedError', error);
      }
    },
    async createBrandedLink({ commit }, { payload }) {
      commit('setBrandedLinkCreatedLoading', true);
      try {
        await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/email/domainauth/branded-link`,
          payload,
        );
        commit('setBrandedLinkCreated', true);
        commit('setBrandedLinkCreatedLoading', false);
      } catch (error) {
        if (error.data && error.data.error) {
          commit('setBrandedLinkCreatedError', error.data.error);
          return;
        }

        commit('setBrandedLinkCreatedError', error);
      }
    },
    async verifyDomainAuth({ commit }, { payload }) {
      commit('setDomainAuthVerifiedLoading', true);
      try {
        await axios.patch(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/email/domainauth/domain-auth/validate`,
          payload,
        );
        commit('setDomainAuthVerified', true);
        commit('setDomainAuthVerifiedLoading', false);
      } catch (error) {
        if (error.data && error.data.error) {
          commit('setDomainAuthVerifiedError', error.data.error);
          return;
        }

        commit('setDomainAuthVerifiedError', error);
      }
    },
    async verifyBrandedLink({ commit }, { payload }) {
      commit('setBrandedLinkVerifiedLoading', true);
      try {
        await axios.patch(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/email/domainauth/branded-link/validate`,
          payload,
        );
        commit('setBrandedLinkVerified', true);
        commit('setBrandedLinkVerifiedLoading', false);
      } catch (error) {
        if (error.data && error.data.error) {
          commit('setBrandedLinkVerifiedError', error.data.error);
          return;
        }

        commit('setBrandedLinkVerifiedError', error);
      }
    },
    async createDomainAuthAndBrandedLink({ dispatch }, { domainAuth, brandedLink }) {
      await dispatch('createDomainAuth', { payload: domainAuth});

      await dispatch('createBrandedLink', { payload: brandedLink});

      dispatch('getAuthDomainInfo');
    },
    clearCreateDomainAuthAndBrandedLinkStates({ commit }) {
      commit('setDomainAuthCreatedError', null);
      commit('setBrandedLinkCreatedError', null);
      commit('setDomainAuthCreated', false);
      commit('setBrandedLinkCreated', false);
    },
    async verifyDomainAuthAndBrandedLink(
      { commit, dispatch, getters },
      { domainAuth, brandedLink },
    ) {
        if (!getters.allowCallVerify) {
          return;
        }

        // Only dispatch verifyDomainAuth if domainAuth is not an empty object
        if (Object.keys(domainAuth).length > 0) {
          await dispatch('verifyDomainAuth', { payload: domainAuth });
        }

        // Only dispatch verifyBrandedLink if brandedLink is not an empty object
        if (Object.keys(brandedLink).length > 0) {
          await dispatch('verifyBrandedLink', { payload: brandedLink });
        }
        
        commit('setNowtLastVerifyTime');

        dispatch('getAuthDomainInfo');
      },
    clearVerifyDomainAuthAndBrandedLinkStates({ commit }) {
      commit('setDomainAuthVerifiedError', null);
      commit('setBrandedLinkVerifiedError', null);
      commit('setDomainAuthVerified', false);
      commit('setBrandedLinkVerified', false);
    },
    async sendDNSInfoTo3rdParty({ commit }, { email }) {
      commit('setInfoTo3rdPartyLoading', true);
      const payload = {
        email: email,
      };
      try {
        await axios.post(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/email/domainauth/send-dns-info`,
          payload,
        );
        commit('setInfoTo3rdPartyLoading', false);
        commit('setInfoTo3rdPartySucess', true);
      } catch (error) {
        if (error.data && error.data.error) {
          commit('setInfoTo3rdPartyLoading', false);
          commit('setInfoTo3rdPartyError', error.data.error);
          return;
        }
        commit('setInfoTo3rdPartyLoading', false);
        commit('setInfoTo3rdPartyError', error);
      }
    },
    async getSystemDomain({ commit }) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/email/domainauth/system`,
        );

        commit('setSystemDomain', response.data);
      } catch (error) {
        if (error.data && error.data.error) {
          commit('setError', error.data.error);
          return;
        }

        commit('setError', error);
      }
    }
  },
};
